import { HttpClient } from '@angular/common/http'
import { inject, Injectable } from '@angular/core'
import { QueryObserverResult } from '@tanstack/query-core'
import { Observable } from 'rxjs'
import type { CitationMinuteNodes } from '../../../../../backend/src/citations/minutes-citations-api.dto'
import { injectMutation, injectQuery, injectQueryClient } from '../../query'
import { queryKeys } from '../../query-keys'
import { ToastService } from '../../toast.service'

export type CallEntityMinutesStatus = 'NOT_GENERATED' | 'ONGOING' | 'COMPLETED' | 'FAILED'

export type CallEntityInterval = {
  id: number
  /** The agenda item this is the entity's discussion of; null for the entity as a whole. */
  templateItemId: number | null
  startSeconds: number
  endSeconds: number
}

export type CallEntity = {
  id: number
  name: string
  orderIndex: number
  intervals: CallEntityInterval[]
  hasMinutes: boolean
  minutesStatus: CallEntityMinutesStatus
}

export type CallEntityTemplateItem = {
  templateItemId: number
  parentId: number | null
  title: string
  index: number
  isEntitySpecific: boolean
  applicableCallEntityIds: number[]
}

export type CallEntitiesOverview = {
  entities: CallEntity[]
  templateItems: CallEntityTemplateItem[]
  agendaEntityNamesToImport: string[]
  hasCallMinutes: boolean
}

export type CallEntityIntervalInput = {
  startSeconds: number
  endSeconds: number
}

const ONGOING_POLL_INTERVAL_MS = 5000

/** `hh:mm:ss`, `mm:ss` or plain seconds, as the meeting's timeline shows them; null for anything else. */
export function parseTimestamp(text: string): number | null {
  const parts = text.trim().split(':')
  if (parts.length > 3 || parts.some((part) => !/^\d+$/.test(part))) {
    return null
  }
  return parts.map(Number).reduce((total, part) => total * 60 + part, 0)
}

@Injectable({ providedIn: 'root' })
export class CallEntitiesService {
  private http = inject(HttpClient)
  private query = injectQuery()
  private mutation = injectMutation()
  private queryClient = injectQueryClient()
  private toastService = inject(ToastService)

  getOverview(callId: number): Observable<QueryObserverResult<CallEntitiesOverview>> {
    return this.query({
      queryKey: queryKeys.calls.entities.byCallId(callId),
      queryFn: () => this.http.get<CallEntitiesOverview>(`/api/calls/${callId}/entities`),
      refetchInterval: (query) => ((query.state.data?.entities ?? []).some((entity) => entity.minutesStatus === 'ONGOING') ? ONGOING_POLL_INTERVAL_MS : false),
    }).result$
  }

  getCitations(callId: number, callEntityId: number): Observable<QueryObserverResult<{ citations: CitationMinuteNodes | null }>> {
    return this.query({
      queryKey: queryKeys.calls.entities.citations(callId, callEntityId),
      queryFn: () => this.http.get<{ citations: CitationMinuteNodes | null }>(`/api/calls/${callId}/entities/${callEntityId}/citations`),
    }).result$
  }

  createEntityMutation() {
    return this.entityMutation(({ callId, name }: { callId: number; name: string }) =>
      this.http.post<{ id: number }>(`/api/calls/${callId}/entities`, { name })
    )
  }

  importFromAgendaMutation() {
    return this.entityMutation(({ callId }: { callId: number }) =>
      this.http.post<{ createdCount: number }>(`/api/calls/${callId}/entities/import-from-agenda`, {})
    )
  }

  renameEntityMutation() {
    return this.entityMutation(({ callId, callEntityId, name }: { callId: number; callEntityId: number; name: string }) =>
      this.http.patch<void>(`/api/calls/${callId}/entities/${callEntityId}`, { name })
    )
  }

  reorderEntitiesMutation() {
    return this.entityMutation(({ callId, callEntityIds }: { callId: number; callEntityIds: number[] }) =>
      this.http.put<void>(`/api/calls/${callId}/entities/order`, { callEntityIds })
    )
  }

  deleteEntityMutation() {
    return this.entityMutation(({ callId, callEntityId }: { callId: number; callEntityId: number }) =>
      this.http.delete<void>(`/api/calls/${callId}/entities/${callEntityId}`)
    )
  }

  replaceIntervalsMutation() {
    return this.entityMutation(
      ({
        callId,
        callEntityId,
        templateItemId,
        intervals,
      }: {
        callId: number
        callEntityId: number
        templateItemId: number | null
        intervals: CallEntityIntervalInput[]
      }) => this.http.put<void>(`/api/calls/${callId}/entities/${callEntityId}/intervals`, { templateItemId, intervals })
    )
  }

  updateTemplateItemScopeMutation() {
    return this.entityMutation(
      ({
        callId,
        templateItemId,
        isEntitySpecific,
        applicableCallEntityIds,
      }: {
        callId: number
        templateItemId: number
        isEntitySpecific: boolean
        applicableCallEntityIds: number[]
      }) => this.http.put<void>(`/api/calls/${callId}/entities/template-items/${templateItemId}`, { isEntitySpecific, applicableCallEntityIds })
    )
  }

  generateEntityMinutesMutation() {
    return this.entityMutation(
      ({ callId, callEntityId }: { callId: number; callEntityId: number }) =>
        this.http.post<void>(`/api/calls/${callId}/entities/${callEntityId}/generate-minutes`, {}),
      'CALL_ENTITIES.GENERATION_STARTED'
    )
  }

  generateAllEntityMinutesMutation() {
    return this.entityMutation(
      ({ callId }: { callId: number }) => this.http.post<{ dispatchedCount: number }>(`/api/calls/${callId}/entities/generate-minutes`, {}),
      'CALL_ENTITIES.GENERATION_STARTED'
    )
  }

  private entityMutation<TVariables extends { callId: number }, TData>(
    mutationFn: (variables: TVariables) => Observable<TData>,
    successToastKey: string | null = null
  ) {
    return this.mutation({
      mutationFn,
      onSuccess: (_, { callId }) => {
        // The call's own key: its meeting details list the entities too, and the entities' key is under it.
        this.queryClient.invalidateQueries({ queryKey: queryKeys.calls.byId(callId) })
        if (successToastKey !== null) {
          this.toastService.show(successToastKey)
        }
      },
      onError: () => {
        this.toastService.show('CALL_ENTITIES.UPDATE_FAILED')
      },
    })
  }
}
